import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useWikiPage } from '@/hooks/useWikiPage';
import { WikiEditorTree } from '@/components/posit/WikiEditorTree';
import { useWikiMutations } from '@/hooks/useWikiMutations';

export default function PositWikiPage() {
  const { positId } = useParams<{ positId: string }>();
  const [editMode, setEditMode] = useState(false);
  const { data, isLoading, isError, error, refetch } = useWikiPage(positId);
  const { upsertSection } = useWikiMutations(positId);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-muted-foreground">Loading details…</span>
        </div>
      </DashboardLayout>
    );
  }

  if (isError || !data) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load details. {error instanceof Error ? error.message : 'Please try again.'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => refetch()} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </DashboardLayout>
    );
  }

  const handleEditModeToggle = () => setEditMode((v) => !v);

  const seedDefaults = useCallback(async () => {
    if (!positId) return;
    const starters = [
      { title: 'Overview', content_md: 'A concise summary about me and my work.' },
      { title: 'Skills', content_md: 'List of core skills and areas of expertise.' },
      { title: 'How‑Tos', content_md: 'Step-by-step guides and methods I use.' },
      { title: 'Insights', content_md: 'Lessons learned and reflections.' },
    ];
    for (let i = 0; i < starters.length; i++) {
      const s = starters[i];
      await upsertSection.mutateAsync({
        posit_id: positId,
        title: s.title,
        content_md: s.content_md,
        is_public: true,
        order_index: i,
        update_lead: s.title === 'Overview',
      });
    }
    await refetch();
  }, [positId, refetch, upsertSection]);

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 px-4 md:px-0">
        <nav className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground mb-6">
          <span>Dashboard</span>
          <span>›</span>
          <span>Posits</span>
          <span>›</span>
          <span className="font-medium text-foreground">{data.posit.title}</span>
          <span>›</span>
           <span className="font-medium text-foreground">Details</span>
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleEditModeToggle}>{editMode ? 'Done' : 'Edit details'}</Button>
          <Button variant="ghost" asChild><Link to={`/d/${data.posit.id}/${(data.posit.title || '').toLowerCase().replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-').replace(/-+/g,'-')}`}>View public page</Link></Button>
          <Button variant="outline" onClick={() => refetch()}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
        </div>

        {editMode ? (
          <div className="space-y-4">
            {(!data.sections || data.sections.length === 0) && (
              <div className="border rounded-md p-4 bg-muted/30 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">No sections yet. Seed starter sections?</div>
                <Button size="sm" onClick={seedDefaults}>Seed starter sections</Button>
              </div>
            )}
            <WikiEditorTree positId={data.posit.id} sections={data.sections as any} />
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Use the public page to preview SEO/GEO layout.</div>
            <Button asChild size="sm" variant="secondary"><Link to={`/d/${data.posit.id}/${(data.posit.title || '').toLowerCase().replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-').replace(/-+/g,'-')}`}>Open public page</Link></Button>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
} 